import React, { useState, useMemo } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import {
  LifeBuoy,
  Search,
  RefreshCw,
  CheckCircle2,
  Clock,
  ArrowUpRight,
  MessageSquare,
  ShieldAlert,
  Building2,
  Filter,
  User
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../../api/worker';
import { Issue } from '../../types';
import { cn } from '../../lib/utils';
import { toast } from 'react-hot-toast';

const severityVariant = (severity?: string) =>
  severity === 'CRITICAL' || severity === 'HIGH' ? 'red'
    : severity === 'MEDIUM' ? 'amber'
    : 'gray';

const statusVariant = (status?: string) =>
  status === 'OPEN' ? 'blue'
    : status === 'RESOLVED' || status === 'CLOSED' ? 'green'
    : status === 'ESCALATED' ? 'red'
    : 'purple';

export const AdminTicketsManager: React.FC = () => {
  const { isSuperAdmin, isSupportAdmin } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [severityFilter, setSeverityFilter] = useState<string>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: tickets = [], isLoading, refetch, isFetching } = useQuery<Issue[]>({
    queryKey: ['admin', 'tickets'],
    queryFn: async () => {
      const res = await apiWorker.get('/api/admin/tickets');
      return Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.content) ? res.data.content : []);
    },
    enabled: isSuperAdmin || isSupportAdmin
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, resolutionNotes }: { id: string; status: string; resolutionNotes?: string }) => {
      await apiWorker.patch(`/api/admin/tickets/${id}/status`, { status, resolutionNotes });
    },
    onSuccess: (_, variables) => {
      toast.success(`Ticket ${variables.id} moved to ${variables.status}`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'tickets'] });
      queryClient.invalidateQueries({ queryKey: ['issues'] });
    },
    onError: () => toast.error('Failed to update ticket status')
  });

  const count = (status: string) => tickets.filter((t) => t.status === status).length;

  const filteredTickets = useMemo(() => {
    return tickets.filter((t) => {
      if (statusFilter !== 'ALL' && t.status !== statusFilter) return false;
      if (severityFilter !== 'ALL' && t.severity !== severityFilter) return false;
      if (categoryFilter !== 'ALL' && t.category !== categoryFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesId = t.id?.toLowerCase().includes(q);
        const matchesTitle = t.title?.toLowerCase().includes(q);
        const matchesAuthor = t.authorName?.toLowerCase().includes(q) || t.requesterId?.toLowerCase().includes(q);
        const matchesOrg = t.addressedToName?.toLowerCase().includes(q) || t.originNodeName?.toLowerCase().includes(q);
        const matchesDesc = t.description?.toLowerCase().includes(q);
        if (!matchesId && !matchesTitle && !matchesAuthor && !matchesOrg && !matchesDesc) {
          return false;
        }
      }
      return true;
    });
  }, [tickets, statusFilter, severityFilter, categoryFilter, searchQuery]);

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header & Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-[#0044FF]" />
            Platform Support Tickets
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Global ticket queue, cross-organization escalations, and platform-level support requests.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search ID, title, requester, org..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-white dark:bg-black border-2 border-black dark:border-white rounded-none outline-none focus:ring-2 focus:ring-[#0044FF] text-black dark:text-white"
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            className="border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] gap-1.5 text-xs font-bold uppercase tracking-wider"
            disabled={isFetching}
          >
            <RefreshCw size={13} className={cn(isFetching ? "animate-spin" : "")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Bauhaus Status Filter Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <button
          type="button"
          onClick={() => setStatusFilter('ALL')}
          className={cn(
            "p-3 text-left border-2 transition-all rounded-none relative flex flex-col justify-between",
            statusFilter === 'ALL'
              ? "border-black dark:border-white bg-black text-white dark:bg-white dark:text-black shadow-[4px_4px_0px_#0044FF]"
              : "border-black dark:border-white bg-white dark:bg-slate-900 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-slate-800 shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
          )}
        >
          <div className="text-[10px] uppercase font-mono tracking-widest font-bold opacity-80">All Tickets</div>
          <div className="text-2xl font-black font-display mt-2">{tickets.length}</div>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('OPEN')}
          className={cn(
            "p-3 text-left border-2 transition-all rounded-none border-t-4 border-t-[#0044FF] flex flex-col justify-between",
            statusFilter === 'OPEN'
              ? "border-black dark:border-white bg-blue-600 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
              : "border-black dark:border-white bg-white dark:bg-slate-900 text-gray-900 dark:text-white hover:bg-blue-50/50 dark:hover:bg-slate-800 shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
          )}
        >
          <div className="text-[10px] uppercase font-mono tracking-widest font-bold">Open</div>
          <div className="text-2xl font-black font-display mt-2">{count('OPEN')}</div>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('INVESTIGATING')}
          className={cn(
            "p-3 text-left border-2 transition-all rounded-none border-t-4 border-t-purple-600 flex flex-col justify-between",
            statusFilter === 'INVESTIGATING'
              ? "border-black dark:border-white bg-purple-600 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
              : "border-black dark:border-white bg-white dark:bg-slate-900 text-gray-900 dark:text-white hover:bg-purple-50/50 dark:hover:bg-slate-800 shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
          )}
        >
          <div className="text-[10px] uppercase font-mono tracking-widest font-bold">Investigating</div>
          <div className="text-2xl font-black font-display mt-2">{count('INVESTIGATING')}</div>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('ESCALATED')}
          className={cn(
            "p-3 text-left border-2 transition-all rounded-none border-t-4 border-t-red-600 flex flex-col justify-between",
            statusFilter === 'ESCALATED'
              ? "border-black dark:border-white bg-red-600 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
              : "border-black dark:border-white bg-white dark:bg-slate-900 text-gray-900 dark:text-white hover:bg-red-50/50 dark:hover:bg-slate-800 shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
          )}
        >
          <div className="text-[10px] uppercase font-mono tracking-widest font-bold">Escalated</div>
          <div className="text-2xl font-black font-display mt-2">{count('ESCALATED')}</div>
        </button>

        <button
          type="button"
          onClick={() => setStatusFilter('RESOLVED')}
          className={cn(
            "p-3 text-left border-2 transition-all rounded-none border-t-4 border-t-green-600 flex flex-col justify-between col-span-2 sm:col-span-1",
            statusFilter === 'RESOLVED'
              ? "border-black dark:border-white bg-green-700 text-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]"
              : "border-black dark:border-white bg-white dark:bg-slate-900 text-gray-900 dark:text-white hover:bg-green-50/50 dark:hover:bg-slate-800 shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]"
          )}
        >
          <div className="text-[10px] uppercase font-mono tracking-widest font-bold">Resolved</div>
          <div className="text-2xl font-black font-display mt-2">{count('RESOLVED') + count('CLOSED')}</div>
        </button>
      </div>

      {/* Secondary Filter Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-white dark:bg-slate-900 border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs font-mono font-bold uppercase text-gray-600 dark:text-gray-400">
            <Filter size={13} />
            Filters:
          </div>

          <div className="flex items-center gap-1">
            <label className="text-[11px] font-mono text-gray-500 uppercase">Severity:</label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="text-xs font-mono font-bold border border-black dark:border-white bg-white dark:bg-slate-950 px-2 py-1 rounded-none outline-none cursor-pointer"
            >
              <option value="ALL">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>

          <div className="flex items-center gap-1">
            <label className="text-[11px] font-mono text-gray-500 uppercase">Category:</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="text-xs font-mono font-bold border border-black dark:border-white bg-white dark:bg-slate-950 px-2 py-1 rounded-none outline-none cursor-pointer"
            >
              <option value="ALL">All Categories</option>
              <option value="TECHNICAL">Technical</option>
              <option value="BILLING">Billing</option>
              <option value="COMPLIANCE">Compliance</option>
              <option value="CONDUCT">Conduct</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
        </div>

        {(statusFilter !== 'ALL' || severityFilter !== 'ALL' || categoryFilter !== 'ALL' || searchQuery.trim()) && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter('ALL');
              setSeverityFilter('ALL');
              setCategoryFilter('ALL');
              setSearchQuery('');
            }}
            className="text-xs font-mono font-bold text-red-600 dark:text-red-400 hover:underline uppercase"
          >
            Reset Filters
          </button>
        )}
      </div>

      {/* Main Ticket Queue Content */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Spinner size="lg" />
          <p className="mt-4 text-xs font-mono uppercase tracking-widest text-gray-500">Loading Support Queue...</p>
        </div>
      ) : filteredTickets.length === 0 ? (
        <Card className="flex flex-col items-center justify-center p-12 text-center border-2 border-black dark:border-white bg-white dark:bg-slate-900 rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
          <div className="w-16 h-16 bg-[#FFD600] border-2 border-black flex items-center justify-center mb-4 shadow-[2px_2px_0px_#000000]">
            <CheckCircle2 size={32} className="text-black" />
          </div>
          <h3 className="font-display font-black text-xl uppercase tracking-tight text-gray-900 dark:text-white">
            No Support Tickets Found
          </h3>
          <p className="text-xs text-gray-500 max-w-sm mt-2 font-sans">
            {tickets.length === 0
              ? 'All incoming platform issues and escalated member tickets are currently cleared.'
              : 'No tickets matched your current search and filter selections.'}
          </p>
          {(statusFilter !== 'ALL' || severityFilter !== 'ALL' || categoryFilter !== 'ALL' || searchQuery.trim()) && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setStatusFilter('ALL');
                setSeverityFilter('ALL');
                setCategoryFilter('ALL');
                setSearchQuery('');
              }}
              className="mt-4 text-xs font-bold uppercase rounded-none border-2 border-black dark:border-white"
            >
              Clear Active Filters
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTickets.map((t) => {
            const isCritical = t.severity === 'CRITICAL';
            const isHigh = t.severity === 'HIGH';
            return (
              <div
                key={t.id}
                data-testid="ticket-row"
                data-id={t.id}
                className={cn(
                  "p-5 bg-white dark:bg-slate-900 border-2 border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition-all hover:translate-x-0.5",
                  isCritical ? "border-l-8 border-l-red-600" : isHigh ? "border-l-8 border-l-orange-500" : "border-l-8 border-l-blue-600"
                )}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left Column: Metadata & Details */}
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold bg-black text-white dark:bg-white dark:text-black px-2 py-0.5 border border-black dark:border-white">
                        #{t.id}
                      </span>
                      <Badge variant={severityVariant(t.severity)} className="rounded-none border border-black dark:border-white font-mono uppercase text-[10px]">
                        {t.severity}
                      </Badge>
                      <Badge
                        data-testid="status-badge"
                        data-status={t.status}
                        variant={statusVariant(t.status)}
                        className="rounded-none border border-black dark:border-white font-mono uppercase text-[10px]"
                      >
                        {t.status}
                      </Badge>
                      {t.category && (
                        <span className="text-[10px] font-mono uppercase tracking-wider bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 px-2 py-0.5 border border-black/20 dark:border-white/20">
                          {t.category}
                        </span>
                      )}
                      {t.escalatedToSystem && (
                        <Badge variant="red" className="rounded-none border border-red-700 font-mono text-[10px] uppercase tracking-wider font-bold">
                          <ShieldAlert size={12} className="mr-1 inline" />
                          Platform Root Queue
                        </Badge>
                      )}
                    </div>

                    <h3 className="font-display font-bold text-lg text-gray-900 dark:text-white leading-snug">
                      {t.title}
                    </h3>

                    {t.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-sans line-clamp-2">
                        {t.description}
                      </p>
                    )}

                    {t.resolutionNotes && (
                      <div className="p-2.5 bg-green-50 dark:bg-green-950/30 border border-green-500 text-green-800 dark:text-green-300 text-xs font-sans">
                        <strong className="font-bold uppercase font-mono mr-1">Resolution:</strong> {t.resolutionNotes}
                      </div>
                    )}

                    {/* Metadata chips */}
                    <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap pt-1 font-mono">
                      <span className="flex items-center gap-1">
                        <User size={13} className="text-gray-400" />
                        From: <strong className="text-gray-700 dark:text-gray-300 font-sans">{t.authorName || t.requesterId || 'Unknown'}</strong>
                      </span>
                      {t.originNodeName && (
                        <span className="flex items-center gap-1">
                          <Building2 size={13} className="text-gray-400" />
                          Origin: <span className="text-gray-700 dark:text-gray-300">{t.originNodeName}</span>
                        </span>
                      )}
                      {t.addressedToName && (
                        <span className="flex items-center gap-1">
                          <ArrowUpRight size={13} className="text-blue-500" />
                          Assigned Org: <span className="text-blue-600 dark:text-blue-400 font-bold">{t.addressedToName}</span>
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock size={13} className="text-gray-400" />
                        Updated {t.updatedAt ? new Date(t.updatedAt).toLocaleDateString() : 'recently'}
                      </span>
                    </div>
                  </div>

                  {/* Right Column: Actions */}
                  <div className="flex flex-row lg:flex-col items-end gap-2 shrink-0 border-t lg:border-t-0 pt-3 lg:pt-0">
                    <Button
                      data-testid="support-ticket-action-btn"
                      variant="primary"
                      size="sm"
                      className="gap-2 bg-black text-white hover:bg-[#0044FF] border-2 border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF] text-xs font-bold uppercase tracking-wider px-4"
                      onClick={() => navigate(`/support?ticket=${t.id}`)}
                    >
                      <MessageSquare size={14} /> Open Thread
                    </Button>

                    <div className="flex items-center gap-1.5 text-xs font-mono">
                      <span className="text-[10px] uppercase text-gray-400 font-bold">Status:</span>
                      <select
                        value={t.status}
                        disabled={updateStatusMutation.isPending}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          let notes: string | undefined = undefined;
                          if (newStatus === 'RESOLVED' || newStatus === 'CLOSED') {
                            notes = window.prompt("Resolution Notes (optional):") || undefined;
                          }
                          updateStatusMutation.mutate({ id: t.id, status: newStatus, resolutionNotes: notes });
                        }}
                        className="text-xs font-mono font-bold uppercase border-2 border-black dark:border-white bg-white dark:bg-slate-950 px-2 py-1 rounded-none outline-none cursor-pointer"
                      >
                        <option value="OPEN">OPEN</option>
                        <option value="INVESTIGATING">INVESTIGATING</option>
                        <option value="ESCALATED">ESCALATED</option>
                        <option value="RESOLVED">RESOLVED</option>
                        <option value="CLOSED">CLOSED</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
